import { useRef } from 'react';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getRedesignedLoanFeesAction } from 'store/loanApplications/actions';

import { ENTITY_TYPE_HREF_MAP } from '../../../EditModal.hrefs';

/**
 * Fetches GET /loan/{loanId}/fee/v3 while the Loan & Lock subtab is active.
 *
 * DEV-5348: a failed fetch here used to be redispatched in a tight, unbounded retry loop. The
 * root cause of the repeated re-firing could not be pinned down with confidence (dispatch,
 * setIsLoading, loanId, and subtab all read as referentially/value-stable on static analysis),
 * so per the ticket's own fallback this ref-guard is the authoritative backstop: once a fetch
 * has failed for a given loanId, it will not be re-dispatched for that loanId again, even if
 * this hook's caller re-renders with an unstable dependency (e.g. a new setIsLoading identity).
 */
const useRedesignedLoanFeesFetch = ({ dispatch, loanId, setIsLoading, subtab }) => {
  const failedLoanIdRef = useRef(null);

  useEffectSafeStrictMode(() => {
    if (subtab !== ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK) {
      return;
    }

    if (failedLoanIdRef.current === loanId) {
      return;
    }

    setIsLoading(true);
    dispatch(getRedesignedLoanFeesAction({ loanId }))
      .catch(() => {
        failedLoanIdRef.current = loanId;
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, loanId, setIsLoading, subtab]);
};

export default useRedesignedLoanFeesFetch;
